import Typography from '@cobliteam/ui/components/Typography';
import { Link } from 'react-router-dom';

interface DataTableCellVehicleProps {
  licensePlate?: string | null;
  brand?: string | null;
  model?: string | null;
  alias?: string | null;
  groupName?: string;
  showGroupName?: boolean;
  showAlias?: boolean;
  icon?: React.ReactNode;
  /** When provided, the cell content is wrapped in a Link and uses link color */
  to?: { pathname: string; search?: string; hash?: string; state?: unknown };
}

/**
 * Renders a vehicle identifier (plate + brand + model, optional alias and group). Column
 * defs using this cell should set `meta.cellType: 'vehicle'` so full plate text doesn't
 * truncate at the default header-derived width.
 */
const DataTableCellVehicle = ({ brand, model, licensePlate, alias, groupName, showGroupName = true, showAlias = false, icon, to }: DataTableCellVehicleProps) => {
  const formattedBrand = brand ? `- ${brand}` : '';
  const formattedModel = model ? `- ${model}` : '';
  const formattedLicensePlate = licensePlate ? ` ${licensePlate}` : '';
  const formattedAlias = alias && showAlias ? `${alias}` : '';

  const vehicleLabel = `${formattedLicensePlate} ${formattedBrand} ${formattedModel}`.trim() || '---';

  const content = (
    <>
      <div className='flex items-center gap-2'>
        {icon}
        <Typography title={vehicleLabel} color='secondary' variant='description-title' className={`line-clamp-1 ${to ? 'text-primary-9' : ''}`}>
          {vehicleLabel}
        </Typography>
      </div>
      {showAlias ? (
        <Typography title={alias} color='secondary' variant='caption-default' className='line-clamp-1'>
          {formattedAlias?.trim() ? formattedAlias : null}
        </Typography>
      ) : null}
      {showGroupName ? (
        <Typography title={groupName} color='secondary' variant='caption-default' className='line-clamp-1'>
          {groupName || '---'}
        </Typography>
      ) : null}
    </>
  );

  const wrapperClassName = 'flex flex-col gap-2';
  const linkClassName = 'inline-flex flex-col gap-2 rounded focus:outline-none';

  if (to) {
    return (
      <Link to={to} className={linkClassName}>
        {content}
      </Link>
    );
  }

  return <div className={wrapperClassName}>{content}</div>;
};

export default DataTableCellVehicle;
